import { useTranslation } from "react-i18next";
import {
  CheckCircle2, AlertTriangle, Plus, Trash2, ShieldCheck, EyeOff,
  Layers, Wrench, Circle, CircleDot, Ruler, Info, Camera, Database,
} from "lucide-react";

const RAW_FORMS = [
  "round_bar", "square_bar", "rect_bar", "hex_bar", "tube",
  "sheet", "block", "casting", "forging", "unknown",
];

const PROCESSES = [
  "turning", "milling", "drilling", "threading", "tapping", "boring",
  "grooving", "knurling", "grinding", "wire_edm", "sinker_edm",
  "sheet_cutting", "bending", "deburring",
];

const CONF_TONE = {
  user_confirmed: "text-emerald-300 border-emerald-500/50 bg-emerald-500/10",
  high: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10",
  medium: "text-blue-300 border-blue-500/40 bg-blue-500/10",
  low: "text-amber-300 border-amber-500/40 bg-amber-500/10",
};

const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

function ConfChip({ level, testid }) {
  const { t } = useTranslation();
  if (!level) return null;
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded border font-mono-data uppercase tracking-wider ${CONF_TONE[level] || CONF_TONE.medium}`}
      data-testid={testid}
    >
      {t(`extractionConfirm.conf.${level}`, { defaultValue: level })}
    </span>
  );
}

function Section({ icon: Icon, title, level, testid, children, actions }) {
  return (
    <div className="border-t border-slate-800 pt-4 first:border-0 first:pt-0" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2 mb-2.5">
        <Icon size={13} className="text-slate-400 shrink-0" />
        <span className="label-tech text-[10px] text-slate-300">{title}</span>
        <ConfChip level={level} testid={`${testid}-conf`} />
        <div className="ml-auto shrink-0">{actions}</div>
      </div>
      {children}
    </div>
  );
}

const inputCls =
  "bg-slate-950/70 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100 font-mono-data focus:border-blue-500 focus:outline-none w-full placeholder:text-slate-600";

function DiameterRows({ rows, kind, onChange, tPrefix }) {
  const { t } = useTranslation();
  const update = (i, key, value) => {
    const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r));
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <div className="text-xs text-slate-500 italic" data-testid={`${tPrefix}-empty`}>
          {t("extractionConfirm.noneDetected")}
        </div>
      )}
      {rows.map((d, i) => (
        <div key={`${tPrefix}-${i}`} className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] gap-2 items-center">
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">Ø</span>
            <input
              type="number" step="0.01" min="0"
              className={`${inputCls} pl-6`}
              value={d.value_mm ?? ""}
              onChange={(e) => update(i, "value_mm", numOrNull(e.target.value))}
              data-testid={`${tPrefix}-value-${i}`}
            />
          </div>
          <input
            className={inputCls}
            placeholder={t("extractionConfirm.tolerance")}
            value={d.tolerance ?? ""}
            onChange={(e) => update(i, "tolerance", e.target.value || null)}
            data-testid={`${tPrefix}-tol-${i}`}
          />
          <input
            type="number" step="0.1" min="0"
            className={inputCls}
            placeholder={kind === "internal" ? t("extractionConfirm.depthMm") : t("extractionConfirm.lengthMm")}
            value={(kind === "internal" ? d.depth_mm : d.length_mm) ?? ""}
            onChange={(e) => update(i, kind === "internal" ? "depth_mm" : "length_mm", numOrNull(e.target.value))}
            data-testid={`${tPrefix}-extent-${i}`}
          />
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
            className="p-1.5 rounded text-slate-500 hover:text-rose-300 hover:bg-rose-500/10"
            title={t("extractionConfirm.removeRow")}
            data-testid={`${tPrefix}-remove-${i}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

export default function ExtractionConfirmPanel({
  extraction,
  onChange,
  confirmed = false,
  onConfirm,
  loading = false,
  cached = false,
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="tech-card p-8" data-testid="extraction-confirm-loading">
        <div className="label-tech text-blue-300 mb-3">{t("extractionConfirm.title")}</div>
        <div className="space-y-2">
          <div className="h-3 bg-slate-800 rounded animate-pulse w-2/3" />
          <div className="h-3 bg-slate-800 rounded animate-pulse w-1/2" />
          <div className="h-3 bg-slate-800 rounded animate-pulse w-3/4" />
        </div>
      </div>
    );
  }

  if (!extraction) {
    return (
      <div className="tech-card p-8 text-sm text-slate-400" data-testid="extraction-confirm-empty">
        {t("extractionConfirm.noExtraction")}
      </div>
    );
  }

  const fc = extraction.field_confidence || {};
  const form = extraction.raw_material_form || {};
  const procTypes = (extraction.processes || []).map((p) => p.type);
  const dims = extraction.overall_dimensions_mm || {};
  const warnings = extraction.consistency_warnings || [];
  const views = extraction.views_used || [];

  const patch = (partial) => onChange({ ...extraction, ...partial });

  const setForm = (value) =>
    patch({
      raw_material_form: { ...form, value },
      field_confidence: { ...fc, raw_material_form: "user_confirmed" },
    });

  const toggleProcess = (type) => {
    const has = procTypes.includes(type);
    const next = has
      ? (extraction.processes || []).filter((p) => p.type !== type)
      : [...(extraction.processes || []), { type, reason: "user_added", confidence: "user_confirmed" }];
    patch({ processes: next, field_confidence: { ...fc, processes: "user_confirmed" } });
  };

  const setDiameters = (key, rows) =>
    patch({
      [key]: rows,
      field_confidence: { ...fc, [key]: "user_confirmed" },
    });

  const setThreads = (rows) =>
    patch({ threads: rows, field_confidence: { ...fc, threads: "user_confirmed" } });

  const setDim = (key, value) =>
    patch({
      overall_dimensions_mm: { ...dims, [key]: numOrNull(value) },
      field_confidence: { ...fc, overall_dimensions: "user_confirmed" },
    });

  return (
    <div data-testid="extraction-confirm-panel">
      <div className="label-tech mb-2">{t("common.step")} 2</div>
      <h2 className="font-display font-bold text-xl text-slate-100 mb-1">
        {t("extractionConfirm.title")}
      </h2>
      <p className="text-slate-400 text-sm mb-5 max-w-2xl">{t("extractionConfirm.subtitle")}</p>

      {cached && (
        <div
          className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/[0.07] text-xs text-cyan-300"
          data-testid="extraction-cached-badge"
        >
          <Database size={13} /> {t("extractionConfirm.cachedBadge")}
        </div>
      )}

      {extraction.raster_source && (
        <div
          className="mb-4 flex items-start gap-2.5 px-4 py-3 rounded-md border border-amber-500/40 bg-amber-500/[0.07] text-sm text-amber-200"
          data-testid="extraction-raster-warning"
        >
          <Camera size={15} className="mt-0.5 shrink-0" />
          <span>{t("extractionConfirm.rasterWarning")}</span>
        </div>
      )}

      {warnings.length > 0 && (
        <div
          className="mb-4 px-4 py-3 rounded-md border border-amber-500/40 bg-amber-500/[0.05]"
          data-testid="extraction-consistency-warnings"
        >
          <div className="flex items-center gap-2 mb-1.5 text-amber-300">
            <AlertTriangle size={14} />
            <span className="label-tech text-[10px]">{t("extractionConfirm.autoChecks")}</span>
          </div>
          <ul className="space-y-1 text-xs text-amber-200">
            {warnings.map((w) => (
              <li key={`cw-${w}`} data-testid={`extraction-warning-${w}`}>
                · {t(`extractionConfirm.warn.${w}`, { defaultValue: w.replace(/_/g, " ") })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="tech-card p-4 sm:p-6 space-y-5">
        <Section icon={Layers} title={t("extractionConfirm.rawForm")} level={fc.raw_material_form} testid="section-raw-form">
          <select
            className={`${inputCls} max-w-sm`}
            value={form.value || "unknown"}
            onChange={(e) => setForm(e.target.value)}
            data-testid="raw-form-select"
          >
            {RAW_FORMS.map((f) => (
              <option key={f} value={f}>{t(`extractionConfirm.forms.${f}`)}</option>
            ))}
          </select>
          {form.evidence && (
            <p className="mt-2 text-xs text-slate-500 flex items-start gap-1.5" data-testid="raw-form-evidence">
              <Info size={12} className="mt-0.5 shrink-0" /> {form.evidence}
            </p>
          )}
        </Section>

        <Section icon={Wrench} title={t("extractionConfirm.processes")} level={fc.processes} testid="section-processes">
          <div className="flex flex-wrap gap-2">
            {PROCESSES.map((p) => {
              const on = procTypes.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggleProcess(p)}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    on
                      ? "border-blue-500/60 bg-blue-500/15 text-blue-200"
                      : "border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300"
                  }`}
                  data-testid={`process-chip-${p}`}
                  aria-pressed={on}
                >
                  {t(`extractionConfirm.proc.${p}`)}
                </button>
              );
            })}
          </div>
        </Section>

        <Section
          icon={Circle}
          title={t("extractionConfirm.externalDiameters")}
          level={fc.external_diameters}
          testid="section-external-diameters"
          actions={
            <button
              type="button"
              onClick={() => setDiameters("external_diameters", [...(extraction.external_diameters || []), { value_mm: null, tolerance: null, length_mm: null, confidence: "user_confirmed" }])}
              className="text-[11px] px-2 py-0.5 rounded border border-slate-700 text-slate-400 hover:border-blue-500 hover:text-blue-300 inline-flex items-center gap-1"
              data-testid="add-external-diameter"
            >
              <Plus size={11} /> {t("extractionConfirm.addRow")}
            </button>
          }
        >
          <DiameterRows
            rows={extraction.external_diameters || []}
            kind="external"
            tPrefix="ext-dia"
            onChange={(rows) => setDiameters("external_diameters", rows)}
          />
        </Section>

        <Section
          icon={CircleDot}
          title={t("extractionConfirm.internalDiameters")}
          level={fc.internal_diameters}
          testid="section-internal-diameters"
          actions={
            <button
              type="button"
              onClick={() => setDiameters("internal_diameters", [...(extraction.internal_diameters || []), { value_mm: null, tolerance: null, depth_mm: null, through: null, confidence: "user_confirmed" }])}
              className="text-[11px] px-2 py-0.5 rounded border border-slate-700 text-slate-400 hover:border-blue-500 hover:text-blue-300 inline-flex items-center gap-1"
              data-testid="add-internal-diameter"
            >
              <Plus size={11} /> {t("extractionConfirm.addRow")}
            </button>
          }
        >
          <DiameterRows
            rows={extraction.internal_diameters || []}
            kind="internal"
            tPrefix="int-dia"
            onChange={(rows) => setDiameters("internal_diameters", rows)}
          />
        </Section>

        <Section
          icon={Wrench}
          title={t("extractionConfirm.threads")}
          level={fc.threads}
          testid="section-threads"
          actions={
            <button
              type="button"
              onClick={() => setThreads([...(extraction.threads || []), { designation: "", quantity: 1, depth_mm: null, confidence: "user_confirmed" }])}
              className="text-[11px] px-2 py-0.5 rounded border border-slate-700 text-slate-400 hover:border-blue-500 hover:text-blue-300 inline-flex items-center gap-1"
              data-testid="add-thread"
            >
              <Plus size={11} /> {t("extractionConfirm.addRow")}
            </button>
          }
        >
          <div className="space-y-2">
            {(extraction.threads || []).length === 0 && (
              <div className="text-xs text-slate-500 italic" data-testid="threads-empty">
                {t("extractionConfirm.noneDetected")}
              </div>
            )}
            {(extraction.threads || []).map((th, i) => (
              <div key={`th-${i}`} className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.8fr)_minmax(0,1fr)_auto] gap-2 items-center">
                <input
                  className={inputCls}
                  placeholder="M6x1"
                  value={th.designation ?? ""}
                  onChange={(e) => setThreads((extraction.threads || []).map((x, idx) => (idx === i ? { ...x, designation: e.target.value } : x)))}
                  data-testid={`thread-designation-${i}`}
                />
                <input
                  type="number" min="1" step="1"
                  className={inputCls}
                  value={th.quantity ?? 1}
                  onChange={(e) => setThreads((extraction.threads || []).map((x, idx) => (idx === i ? { ...x, quantity: Number(e.target.value) || 1 } : x)))}
                  data-testid={`thread-quantity-${i}`}
                />
                <input
                  type="number" min="0" step="0.1"
                  className={inputCls}
                  placeholder={t("extractionConfirm.depthMm")}
                  value={th.depth_mm ?? ""}
                  onChange={(e) => setThreads((extraction.threads || []).map((x, idx) => (idx === i ? { ...x, depth_mm: numOrNull(e.target.value) } : x)))}
                  data-testid={`thread-depth-${i}`}
                />
                <button
                  type="button"
                  onClick={() => setThreads((extraction.threads || []).filter((_, idx) => idx !== i))}
                  className="p-1.5 rounded text-slate-500 hover:text-rose-300 hover:bg-rose-500/10"
                  data-testid={`thread-remove-${i}`}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Ruler} title={t("extractionConfirm.overall")} level={fc.overall_dimensions} testid="section-overall">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              ["length_mm", t("extractionConfirm.length")],
              ["width_mm", t("extractionConfirm.width")],
              ["height_mm", t("extractionConfirm.height")],
              ["across_flats_mm", t("extractionConfirm.acrossFlats")],
            ].map(([key, label]) => (
              <label key={key} className="block min-w-0">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1 truncate">{label}</span>
                <input
                  type="number" step="0.1" min="0"
                  className={inputCls}
                  value={dims[key] ?? ""}
                  onChange={(e) => setDim(key, e.target.value)}
                  data-testid={`overall-${key}`}
                />
              </label>
            ))}
          </div>
        </Section>

        {views.length > 0 && (
          <Section icon={EyeOff} title={t("extractionConfirm.views")} testid="section-views">
            <div className="flex flex-wrap gap-2">
              {views.map((v) => (
                <span
                  key={`view-${v.kind}`}
                  className={`text-[11px] px-2 py-0.5 rounded border font-mono-data ${
                    v.used_for_dimensions
                      ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300"
                      : "border-slate-700 bg-slate-800/60 text-slate-500 line-through"
                  }`}
                  title={v.used_for_dimensions ? "" : t("extractionConfirm.viewNotUsable")}
                  data-testid={`view-chip-${v.kind}`}
                >
                  {t(`extractionConfirm.views_kind.${v.kind}`, { defaultValue: v.kind })}
                </span>
              ))}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">{t("extractionConfirm.viewsHint")}</p>
          </Section>
        )}

        {(extraction.extraction_notes || []).length > 0 && (
          <Section icon={Info} title={t("extractionConfirm.notes")} testid="section-notes">
            <ul className="space-y-1 text-xs text-slate-400">
              {extraction.extraction_notes.map((n, i) => (
                <li key={`note-${i}`}>· {n}</li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {/* BLOCKING CONFIRMATION */}
      <div
        className={`mt-5 rounded-md border p-4 flex flex-wrap items-start gap-3 ${
          confirmed
            ? "border-emerald-500/50 bg-emerald-500/[0.07]"
            : "border-blue-500/40 bg-blue-500/[0.05]"
        }`}
        data-testid="extraction-confirm-gate"
      >
        {confirmed ? (
          <CheckCircle2 size={18} className="text-emerald-300 mt-0.5 shrink-0" />
        ) : (
          <ShieldCheck size={18} className="text-blue-300 mt-0.5 shrink-0" />
        )}
        <div className="flex-1">
          <div className={`text-sm font-medium ${confirmed ? "text-emerald-200" : "text-slate-100"}`}>
            {confirmed ? t("extractionConfirm.confirmedBadge") : t("extractionConfirm.confirmHint")}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{t("extractionConfirm.confirmBody")}</p>
        </div>
        {!confirmed && (
          <button
            type="button"
            onClick={onConfirm}
            className="btn-primary px-4 py-2 rounded-md text-sm inline-flex items-center gap-2 shrink-0"
            data-testid="confirm-extraction-btn"
          >
            <CheckCircle2 size={14} /> {t("extractionConfirm.confirmCta")}
          </button>
        )}
      </div>
    </div>
  );
}
